import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import _ from 'lodash';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { Link, Loader } from '@jutro/components';
import { LocalDateUtil } from 'gw-portals-util-js';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { EndorsementService } from 'gw-capability-policychange';
import { SgDate } from 'sg-date';
import moment from 'moment';
import { SgCepChanges, SgWebchatUtil } from 'saga-modules';
import SgCepLobSpecificUtil from 'sg-cep-lob-specific-util';
import CepConstants from 'sg-cep-constants';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import SgEndorsementSummaryTile from './sg-endorsement-summary-tile/SgEndorsementSummaryTile';
import SgMotorSelectChanges from './sg-motor-select-changes/SgMotorSelectChanges';
import metadata from './SgEndorsementInfo.metadata.json5';
import messages from './SgEndorsementInfo.messages';

function SgEndorsementInfo(props) {
    const {
        policy,
        modelVM,
        updateData
    } = props;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const [isLoading, setLoadingState] = useState(true);
    const [currentDateSelection, setCurrentDateSelection] = useState('');
    const [showChangeDatePicker, setShowChangeDatePicker] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);

    const availableDateRadios = useMemo(() => {
        return [
            {
                code: 'today',
                date: LocalDateUtil.today(),
                name: translator({
                    id: 'cep.components.SgEndorsementInfo.Today',
                    defaultMessage: ''
                }),
                showPicker: false
            },
            {
                code: 'tomorrow',
                date: LocalDateUtil.today(1),
                name: translator({
                    id: 'cep.components.SgEndorsementInfo.Tomorrow',
                    defaultMessage: ''
                }),
                showPicker: false
            },
            {
                code: 'other',
                date: undefined,
                name: translator({
                    id: 'cep.components.SgEndorsementInfo.Other',
                    defaultMessage: ''
                }),
                showPicker: true
            },
        ];
    }, []);

    const trackChat = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        SgWebchatUtil.trackChat();
    }, []);

    const getWebChatWording = useCallback(() => {
        return (
            <span>
                <p id="webchatMessageEndoresementInfo">
                    {translator(messages.DisableMessageWithWebChatFirst)}
                    <Link
                        href="/"
                        id="webchatlinkEndoresementInfo"
                        onClick={trackChat}
                        className="saga-intercom"
                    >
                        {translator(messages.DisableMessageWithWebChatSecond)}
                    </Link>
                </p>
            </span>
        );
    }, []);

    useEffect(() => {
        if (policy.disabled) {
            setLoadingState(false);
        } else {
            EndorsementService.loadEndorsement(policy.policyNumber)
                .then((result) => {
                    const copy = viewModelService.clone(modelVM);
                    copy.value = result;
                    copy.baseData.effectiveDate.value = {};
                    if (policy.isPrivateMotor) {
                        copy.lobData.privateMotor.selectedChanges.value = {
                            hasVehicleChanges: false,
                            hasDriverChanges: false,
                            hasCoveragesChanges: false
                        };
                    }
                    updateData(copy);
                    SgCepChanges.savePreviousModel(_.cloneDeep(copy.value));
                    setLoadingState(false);
                });
        }
    }, [policy]);

    const handleDateChange = useCallback((value) => {
        const selectedDateValue = _.filter(availableDateRadios, { code: value })[0];
        setCurrentDateSelection(value);
        setShowChangeDatePicker(selectedDateValue.showPicker);
        const tempModelVM = _.clone(modelVM);
        tempModelVM.baseData.effectiveDate.value = _.isEmpty(selectedDateValue.date)
            ? selectedDateValue.date : {
                ...selectedDateValue.date,
                monthViewValue: selectedDateValue.date.month + 1
            };
        if (policy.isPrivateMotor) {
            tempModelVM.lobData.privateMotor.selectedChanges.value = {
                hasVehicleChanges: false,
                hasDriverChanges: false,
                hasCoveragesChanges: false
            };
        }
        updateData(tempModelVM);
    }, [policy, modelVM, updateData]);

    const handleChangeSelect = useCallback((value, selectedChange) => {
        const copyPolicyChange = _.clone(modelVM);
        copyPolicyChange.lobData.privateMotor.selectedChanges.value[selectedChange] = value;
        updateData(copyPolicyChange);
    }, [updateData, modelVM]);

    const policyDisabledMessageContent = useMemo(() => {
        const contactUsPhoneNumber = policy.disableReason === CepConstants.NO_POLICY_CHANGE_REASONS.SPM_THIRD_PARTY_POLICY
            ? SgCepLobSpecificUtil.getProductSpecificMTAContactPhone(policy.productCode)
            : SgCepLobSpecificUtil.getProductSpecificContactPhone(policy.productCode);
        let msgError;
        switch (policy.disableReason) {
            case CepConstants.NO_POLICY_CHANGE_REASONS.HOME_POLICY:
            case CepConstants.NO_POLICY_CHANGE_REASONS.MOTOR_HOME_POLICY:
                return (
                    <>
                        <span>{translator(messages.NoPolicyChangeOnHomeAndMotorHomePolicy)}</span>
                        <Link
                            href="/"
                            id="webchatLink"
                            onClick={trackChat}
                            className="sgWebchatLink saga-intercom"
                            aria-hidden="true"
                        >
                            {translator(messages.WebChatLink)}
                        </Link>
                    </>
                );
            case CepConstants.NO_POLICY_CHANGE_REASONS.SMI_POLICY:
                msgError = messages.NoPolicyChangeOnSmiPolicy;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.TRAVEL_POLICY:
                msgError = messages.NoPolicyChangeOnTravelPolicy;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.CASH_CHEQUE_PAYMENT_PLAN:
                msgError = messages.NoPolicyChangeOnCashChequePaymentPlan;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.SCHEDULED_POLICY:
                msgError = messages.NoPolicyChangeOnPolicyWithScheduledPolicy;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.FUTURE_BOUND_RENEWALS:
                msgError = messages.NoPolicyChangeOnPolicyWithFutureBoundRenewals;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.FUTURE_RENEWALS:
                msgError = messages.NoPolicyChangeOnPolicyWithFutureRenewals;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.OUTSTANDING_PAYMENT:
                msgError = messages.NoPolicyChangeOnPolicyWithOutstandingPayment;
                break;
            case CepConstants.NO_POLICY_CHANGE_REASONS.SPM_THIRD_PARTY_POLICY:
                msgError = messages.NoPolicyChangeOnSpmPolicy;
                break;
            default:
                msgError = messages.DefaultError;
        }
        return (
            <>
                {translator(msgError, {
                    phoneNumber: isMobile
                        ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a> : contactUsPhoneNumber
                })}
            </>
        );
    }, [isMobile, policy, translator]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrides = {
        cepEndorsementPolicySummaryTile: {
            policy: policy
        },
        policyDisabledMessageContainer: {
            visible: policy.disabled
        },
        policyDisabledMessage: {
            visible: policy.disabled,
            content: SgCepLobSpecificUtil.shouldShowWebChatWordingForBlockedEndorsement(policy.productCode)
                ? getWebChatWording()
                : policyDisabledMessageContent,
        },
        backToMainButton: {
            visible: policy.disabled
        },
        policyChangesSection: {
            visible: !policy.disabled
        },
        selectPolicyChanges: {
            visible: modelVM.baseData.effectiveDate.aspects.valid
        },
        selectMotorPolicyChangesSection: {
            visible: policy.isPrivateMotor,
            policyDetails: modelVM.value,
            handleChangeSelect: handleChangeSelect
        },
        effectiveDate: {
            onValueChange: handleDateChange,
            availableValues: availableDateRadios,
            value: currentDateSelection
        },
        effectiveChangeDateField: {
            visible: showChangeDatePicker,
            updateData: updateData,
            modelVM,
            minDate: moment()
                .add(2, 'days')
                .toDate(),
            maxDate: moment()
                .add(30, 'days')
                .toDate(),
            path: 'baseData.effectiveDate',
            showErrors: !_.isEmpty(modelVM.baseData.effectiveDate.value)
                && !_.isEmpty(modelVM.baseData.effectiveDate.aspects.validationMessages),
        }
    };
    const resolvers = {
        resolveComponentMap: {
            SgEndorsementSummaryTile,
            SgMotorSelectChanges,
            SgDate
        }
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

export default SgEndorsementInfo;
